// @systeme: calendrier-ical
// ============================================================================
// PairsForm — « Ma séance » : aiguillage depuis le lien posé dans l'agenda
// ============================================================================
// Cible du lien stable embarqué dans les événements du flux iCal personnel.
// La page est un SAS d'une seconde : elle interroge /api/seance-hub/:token qui
// décide, selon le moment, de l'action disponible — puis redirige vers la page
// existante (/confirmer-presence/:t avant la séance, /emarger/:t du début à
// J+7). Les autres états (avant séance côté animateur, séance annulée, fenêtre
// close) s'affichent ici. Public : l'utilisateur vient de son agenda, pas
// forcément connecté — le token de l'URL porte l'authentification.
// ============================================================================

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Card, CardContent, Typography, CircularProgress, Alert } from "@mui/material";
import { CalendarCheck } from "lucide-react";
import { api, messageApi } from "../lib/api";
import { formatJourSemaineFR, formatHeureFR } from "@cecodev/shared";

interface SeanceMeta {
  groupeNom: string | null;
  numero: number | null;
  dateSeance: string;
  heureDebut: string | null;
  heureFin: string | null;
  statut: string;
}

export default function SeanceHubPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [etat, setEtat] = useState<{ action: string; seance?: SeanceMeta } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    api
      .get(`/seance-hub/${token}`)
      .then((res) => {
        const { action, url, seance } = res.data ?? {};
        if ((action === "CONFIRMATION" || action === "EMARGEMENT") && url) {
          navigate(url, { replace: true });
          return;
        }
        setEtat({ action, seance });
      })
      .catch((e) => {
        setErreur(messageApi(e, "Ce lien n'est plus valide. Retrouvez votre séance depuis votre espace Pairsform."));
      });
  }, [token, navigate]);

  const libelleSeance = (s?: SeanceMeta) =>
    s ? `${formatJourSemaineFR(s.dateSeance)}${s.heureDebut ? ` à ${formatHeureFR(s.heureDebut)}` : ""}` : "";

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f6f8f9", p: 2 }}>
      <Card sx={{ maxWidth: 440, width: "100%" }} variant="outlined">
        <CardContent sx={{ textAlign: "center", py: 4 }}>
          <CalendarCheck size={36} color="#4BB7A5" />
          <Typography variant="h6" fontWeight={700} sx={{ mt: 1, mb: 2 }}>
            Ma séance Pairsform
          </Typography>

          {erreur ? (
            <Alert severity="warning" sx={{ textAlign: "left" }}>{erreur}</Alert>
          ) : !etat ? (
            <>
              <CircularProgress size={28} sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Un instant — nous préparons l'action disponible pour votre séance…
              </Typography>
            </>
          ) : (
            <>
              {etat.seance && (
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  <strong>{etat.seance.groupeNom}</strong>
                  {etat.seance.numero ? ` · Séance ${etat.seance.numero}` : ""}
                  <br />
                  {libelleSeance(etat.seance)}
                </Typography>
              )}
              {etat.action === "AVANT_SEANCE" && (
                <Alert severity="info" sx={{ textAlign: "left" }}>
                  La séance n'a pas encore commencé — l'émargement ouvrira au début de la séance.
                  Revenez sur ce lien depuis votre agenda le moment venu.
                </Alert>
              )}
              {etat.action === "ANNULEE" && (
                <Alert severity="warning" sx={{ textAlign: "left" }}>
                  Cette séance a été annulée. Votre agenda se mettra à jour automatiquement.
                </Alert>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
